import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';

/**
 * POST /api/wallet/connect/create
 * Create Stripe Connect account and get onboarding link
 */

/**
 * Request schema for creating a Stripe Connect account
 */
export const ConnectCreateRequestSchema = z.object({
  /** Country ID for the seller's account (optional, will use default address if not provided) */
  countryId: z.coerce.number().int().positive().optional(),
  /** Sent by the iOS/Android app so Stripe returns the seller to the app, not the website */
  native: z.boolean().optional(),
});

export type ConnectCreateRequest = z.infer<typeof ConnectCreateRequestSchema>;

/**
 * Success response schema for connect create
 */
export const ConnectCreateSuccessSchema = z.object({
  success: z.literal(true),
  /** Onboarding URL for Stripe Connect (only returned when not yet onboarded) */
  url: z.string().url().optional(),
  /** Whether the user is already fully onboarded */
  onboarded: z.boolean().optional(),
});

export type ConnectCreateSuccess = z.infer<typeof ConnectCreateSuccessSchema>;

/**
 * Complete response schema for connect create
 * Discriminated union of success and error responses
 */
export const ConnectCreateResponseSchema = z.discriminatedUnion('success', [
  ConnectCreateSuccessSchema,
  ErrorResponseSchema,
]);

export type ConnectCreateResponse = z.infer<typeof ConnectCreateResponseSchema>;

/**
 * GET /api/wallet/connect/status
 * Get Stripe Connect account status
 */

/** The payout pipeline states derived in utils/stripe-requirements.ts */
export const PayoutStateSchema = z.enum([
  'not_started',
  'incomplete',
  'action_needed',
  'pending',
  'rejected',
  'active',
]);

export type PayoutState = z.infer<typeof PayoutStateSchema>;

/** One humanized thing Stripe is still waiting on */
export const PayoutRequirementSchema = z.object({
  id: z.string(),
  label: z.string(),
  detail: z.string().optional(),
  urgency: z.enum(['past_due', 'currently_due', 'pending_verification']),
  fields: z.array(z.string()),
});

export type PayoutRequirement = z.infer<typeof PayoutRequirementSchema>;

/** One humanized verification failure, with the fix */
export const PayoutErrorSchema = z.object({
  id: z.string(),
  title: z.string(),
  reason: z.string(),
  fix: z.string(),
  requirementLabel: z.string(),
  code: z.string(),
});

export type PayoutError = z.infer<typeof PayoutErrorSchema>;

/**
 * Success response schema for connect status.
 *
 * `onboarded` and `chargesEnabled` are the original booleans and stay for
 * backwards compatibility; everything below them is the richer payout status
 * the wallet page renders, sourced from STRIPE_ACCOUNT_CACHE.
 */
export const ConnectStatusSuccessSchema = z.object({
  success: z.literal(true),
  /** Whether the user has a Stripe Connect account */
  onboarded: z.boolean().optional(),
  /** Whether charges are enabled on the account */
  chargesEnabled: z.boolean().optional(),
  /** Whether Stripe will pay out to the seller's bank */
  payoutsEnabled: z.boolean().optional(),
  /** Whether the seller finished the Stripe onboarding form */
  detailsSubmitted: z.boolean().optional(),
  /** Where the seller stands in the payout pipeline */
  state: PayoutStateSchema.optional(),
  /** Headline for the payout status card */
  title: z.string().optional(),
  /** One-sentence explanation of the state */
  message: z.string().optional(),
  /** Label for the primary action, null when there is nothing to do */
  actionLabel: z.string().nullable().optional(),
  /** Which link the primary action should mint */
  actionKind: z.enum(['onboarding', 'update', 'dashboard', 'support']).nullable().optional(),
  /** What Stripe is still waiting on, grouped and humanized */
  requirements: z.array(PayoutRequirementSchema).optional(),
  /** Verification failures, with a concrete fix for each */
  errors: z.array(PayoutErrorSchema).optional(),
  /** ISO date when Stripe disables the account, if a deadline is set */
  deadline: z.string().nullable().optional(),
  /** True when only support can clear the block */
  terminal: z.boolean().optional(),
  /** When the cached snapshot was last refreshed from Stripe */
  lastSyncedAt: z.string().nullable().optional(),
});

export type ConnectStatusSuccess = z.infer<typeof ConnectStatusSuccessSchema>;

/**
 * Complete response schema for connect status
 * Discriminated union of success and error responses
 */
export const ConnectStatusResponseSchema = z.discriminatedUnion('success', [
  ConnectStatusSuccessSchema,
  ErrorResponseSchema,
]);

export type ConnectStatusResponse = z.infer<typeof ConnectStatusResponseSchema>;

/**
 * POST /api/wallet/connect/link
 * Mint a single-use Stripe link. Account links expire and can only be visited
 * once, so they're minted when the seller clicks, never alongside status.
 */
export const ConnectLinkRequestSchema = z.object({
  /**
   * `onboarding` resumes the setup form, `update` reopens a submitted account
   * to correct information, `dashboard` opens the Stripe Express dashboard.
   */
  kind: z.enum(['onboarding', 'update', 'dashboard']),
  /** Sent by the iOS/Android app so Stripe returns the seller to the app, not the website */
  native: z.boolean().optional(),
});

export type ConnectLinkRequest = z.infer<typeof ConnectLinkRequestSchema>;

export const ConnectLinkSuccessSchema = z.object({
  success: z.literal(true),
  /** The single-use URL to send the seller to */
  url: z.string().url(),
  /** Which kind of link was actually minted */
  kind: z.enum(['onboarding', 'update', 'dashboard']),
});

export type ConnectLinkSuccess = z.infer<typeof ConnectLinkSuccessSchema>;

export const ConnectLinkResponseSchema = z.discriminatedUnion('success', [
  ConnectLinkSuccessSchema,
  ErrorResponseSchema,
]);

export type ConnectLinkResponse = z.infer<typeof ConnectLinkResponseSchema>;

/**
 * GET /api/wallet/balance
 * Live Stripe balance and payout destination for a connected seller.
 */
export const PayoutDestinationSchema = z.object({
  /** e.g. "Wells Fargo" — may be absent on some banks */
  bankName: z.string().nullable(),
  /** Last four of the account number */
  last4: z.string().nullable(),
  /** 'bank_account' or 'card' */
  type: z.string(),
});

export type PayoutDestination = z.infer<typeof PayoutDestinationSchema>;

export const WalletBalanceSuccessSchema = z.object({
  success: z.literal(true),
  /** Cleared funds, in dollars, awaiting the next payout */
  available: z.number(),
  /** Funds from recent sales still settling, in dollars */
  pending: z.number(),
  /** ISO currency code, lowercased as Stripe returns it */
  currency: z.string(),
  /** Where the money lands */
  destination: PayoutDestinationSchema.nullable(),
  /** ISO date of the next scheduled payout, when one is in flight */
  nextPayoutDate: z.string().nullable(),
  /** Amount of the next scheduled payout, in dollars */
  nextPayoutAmount: z.number().nullable(),
});

export type WalletBalanceSuccess = z.infer<typeof WalletBalanceSuccessSchema>;

export const WalletBalanceResponseSchema = z.discriminatedUnion('success', [
  WalletBalanceSuccessSchema,
  ErrorResponseSchema,
]);

export type WalletBalanceResponse = z.infer<typeof WalletBalanceResponseSchema>;
